import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { reuseRows } from '../../../components/rows';
import { formatDate, formatHeight } from '../../../domain';
import { PatientDetailApi } from './patient-detail.api';
import { PATIENT_SHORTCUTS } from '../../../components/patient-analytics';
import { recallPeriod } from '../../../components/period';
import { PatientAnalyticsPresenter } from '../../../components/patient-analytics/patient-analytics.presenter';
import { StatusBandsPresenter } from '../../../components/status-bands/status-bands.presenter';
import { BucketReadingsPresenter } from '../../../components/bucket-readings/bucket-readings.presenter';
import { DAY_FIELDS, DEFAULT_HISTORY_SORT, READING_FIELDS, buildDayRow, buildHistoryColumns, buildReadingRow, historyTableModel } from './history-table';
import { DAY_REPORT_COLUMNS, bucketKey, dayReportHours, dayReportJobs } from './day-report';
import { alcoholLabel, daysOfActivityLabel, healthPlanLabel, healthStateLabel, medicalConsultationsLabel, mouthHealthLabel, physicalActivityLabel, sexLabel, smokingLabel } from './anamnese-labels';
import { AnamneseSectionScope, PatientDetailScope, InfoFieldScope } from './patient-detail.scope';
const LOG = Logger.get('Mormaii.CollaboratorDetailPresenter');
/** Cinquenta por pagina nas duas tabelas, como o `rowsPerPage={50}` da referencia. */
const PER_PAGE = 50;
// @Inject
const detailApi = PatientDetailApi.INSTANCE;
/**
 * Quantas celulas o relatorio do dia busca ao mesmo tempo.
 *
 * O backend so entrega as leituras cruas por celula (tipo x hora), e um dia cheio tem
 * ate ~180 delas - medido no banco local em 30/09/2026: 73 na mediana, 177 no pior
 * dia. Todas de uma vez seriam 180 requisicoes disputando a conexao; uma por vez,
 * segundos parados. Seis e o que o navegador abre por host no HTTP/1.1.
 */
const REPORT_CONCURRENCY = 6;
/**
 * O detalhe de um paciente - o primeiro apresentador GENERICO do sistema.
 *
 * Generico quer dizer: montado em mais de um ponto da arvore, e escrito sem saber
 * em qual. Ele nao nomeia o proprio place nem o do pai; tudo o que depende de "onde
 * eu estou" vem do `mount` (ver o `DetailMount` acima), e as duas
 * montagens sao as duas subclasses no fim deste arquivo.
 *
 * A razao de ser generico e concreta: a mesma tela e aberta pelo ADMIN, a partir da
 * lista de Colaboradores, e pelo PROFISSIONAL, a partir dos Monitoramentos
 * Compartilhados. Com uma montagem so, o Voltar do profissional caia numa lista que
 * ele nem tem no menu - a guarda recusava, o pouso o devolvia aos Monitoramentos, e
 * no caminho se perdia o filtro, a pagina e a ordenacao daquela lista, que tinha
 * sido liberada da memoria.
 *
 * `mount` e um GETTER abstrato, e nao um campo. Acessor vive no prototipo e ja
 * existe quando o construtor desta classe roda; campo de subclasse so nasce depois
 * dele. Um factory chamado no construtor tambem resolveria, mas congelaria o valor
 * errado para sempre se um dia lesse um campo da subclasse - o getter nao tem esse
 * risco.
 *
 * Duas coisas que o painel antigo guardava em `useState` e que aqui vem do
 * endereco: qual paciente, e qual dia do historico esta aberto. A segunda e a mais
 * interessante - expandir um dia passa a ser um link, e o Voltar fecha o dia em vez
 * de sair da tela.
 *
 * O dia aberto e buscado sob demanda e o resultado fica guardado: fechar e reabrir
 * nao volta a rede.
 */
export class PatientDetailPresenter extends CubePresenter {
    /**
     * A origem da ficha, com a empresa em ESCOPO.
     *
     * A empresa vem da casca, que e onde ela e escolhida - o cabecalho. Desde
     * 18/09/2026 o servidor a exige de quem administra mais de uma, e recusa o pedido
     * em vez de escolher uma por conta propria.
     */
    analyticsSource(patientId) {
        return { kind: 'patient', patientId, companyId: this.owner?.companyInScope };
    }
    constructor(app) {
        super(app, new PatientDetailScope());
        this.parentSlot = NOOP_VOID;
        this.historyPage = 1;
        /**
         * O modelo da tabela do historico: a ordem das colunas que a pessoa arrastou, a
         * largura de cada uma, a presa e a ordem das linhas. A escolha gravada antes dele, nas
         * chaves de cada guarda antiga, e lida na primeira vez.
         */
        this.table = historyTableModel('history', () => this.app.currentSession?.id, {
            columns: 'mormaii.historyColumns',
            widths: 'mormaii.columnWidths:history'
        });
        this.targets = [];
        this.days = [];
        /** As leituras de cada dia ja aberto, para reabrir nao voltar a rede. */
        this.readings = new Map();
        /** Onde o painel de analytics esta olhando. */
        this.analytics = { period: '7d', from: '', to: '', metric: 'FREQUENCIA_CARDIACA' };
        /**
         * O painel do paciente, o mesmo pedaco de tela que o historico compartilhado
         * usa. Os filtros dele sao parametros DESTE place - ver `CollaboratorDetailKeys`.
         */
        /**
         * As faixas de status deste paciente - o mesmo editor de "Meus dados".
         *
         * Era o `editParamsCollaborator` do painel antigo, 348 linhas quase identicas as
         * 315 do editor do responsavel. Aqui e um objeto so, com o nivel como parametro.
         */
        this.bandsPresenter = new StatusBandsPresenter(this.update, (count, warnings) => {
            const salvo = `${count} faixa${count === 1 ? '' : 's'} agora vale${count === 1 ? '' : 'm'} ` +
                `só para ${this.scope.name}.`;
            // O vao vai junto com o "salvo": e a ultima chance de ser lido.
            this.app.alert(warnings.length ? 'warning' : 'info', 'Parâmetros do paciente salvos', warnings.length ? `${salvo}\n\n${warnings.join('\n')}` : salvo);
        });
        /** O detalhamento de uma celula do historico - o mesmo dialogo do link publico. */
        this.bucketPresenter = new BucketReadingsPresenter(this.update);
        this.analyticsPresenter = new PatientAnalyticsPresenter(this.update, async (change) => {
            this.analytics = { ...this.analytics, ...change };
            await this.analyticsPresenter.moveTo(this.analyticsSource(this.patientId), this.analytics);
            this.updateHistory();
        }, true);
        this.pending = 0;
    }
    async applyParameters(intent, initialization, last) {
        const keys = this.mount.keys(this.app, intent);
        if (!keys.patientId) {
            LOG.error('Cheguei ao detalhe sem colaborador');
            return false;
        }
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        // A linha clicada, quando a viagem partiu de um clique: o cabecalho aparece
        // preenchido em vez de piscar vazio enquanto os dados completos chegam.
        this.showRow(keys.row);
        if (initialization) {
            this.scope.onBack = this.action(this.onBack);
            this.scope.backLabel = this.mount.backLabel;
            this.scope.analytics = this.analyticsPresenter.scope;
            this.scope.bands = this.bandsPresenter.scope;
            this.scope.onCloseDay = this.action(this.onCloseDay);
            this.scope.bucket = this.bucketPresenter.scope;
            this.scope.onDownloadDay = this.action(this.onDownloadDay);
            this.scope.onPreviousPage = this.action(this.onMoveHistoryPage.bind(this, -1));
            this.scope.onNextPage = this.action(this.onMoveHistoryPage.bind(this, 1));
            this.scope.onGoToPage = this.action(this.onGoToHistoryPage);
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        if (last) {
            /*
             * O cabecalho e o MESMO da lista que hospeda esta montagem.
             *
             * Na referencia a tabela de cabecalhos casa por PREFIXO de rota, e
             * `/collaborators/194` cai em `/collaborators` - o detalhe nao tem
             * cabecalho proprio. Aqui o prefixo virou a montagem, que e o mesmo
             * conceito dito de forma explicita: o profissional passa a ver
             * "Monitoramentos Compartilhados" em vez de "Colaboradores", que e a
             * tela de onde ele veio e a unica das duas que ele tem no menu.
             */
            this.owner?.showSection(this.mount.section, this.mount.title, this.mount.subtitle);
        }
        this.parentSlot(this.scope);
        this.scope.historyLayout = this.table.show(this);
        const changed = this.patientId !== keys.patientId;
        this.patientId = keys.patientId;
        this.day = keys.day;
        this.historySort = keys.historySort;
        this.historyPage = keys.historyPage;
        if (changed) {
            this.readings.clear();
            await this.load(keys.patientId);
            await this.bandsPresenter.load({ patientId: keys.patientId });
        }
        this.showHistory();
        // Sem periodo no endereco, o ultimo escolhido no painel (B-35).
        const lembrado = keys.periodInAddress ? undefined : recallPeriod(PATIENT_SHORTCUTS);
        this.analytics = {
            period: (lembrado?.shortcut ?? keys.period),
            from: lembrado?.from ?? keys.analyticsFrom,
            to: lembrado?.to ?? keys.analyticsTo,
            metric: keys.metric
        };
        await this.analyticsPresenter.moveTo(this.analyticsSource(keys.patientId), this.analytics);
        if (this.day) {
            await this.loadDay(this.day);
        }
        return true;
    }
    publishParameters(intent) {
        const keys = this.mount.keys(this.app, intent);
        keys.patientId = this.patientId;
        keys.day = this.day;
        keys.historySort = this.historySort;
        keys.historyPage = this.historyPage;
        // O atalho primeiro: os setters das pontas o consultam para decidir se
        // precisam aparecer no endereco.
        keys.period = this.analytics.period;
        keys.analyticsFrom = this.analytics.from;
        keys.analyticsTo = this.analytics.to;
        keys.metric = this.analytics.metric;
    }
    async load(patientId) {
        const token = ++this.pending;
        this.scope.loading = true;
        this.scope.error = undefined;
        try {
            // Os tres em paralelo: nenhum depende do outro, e em serie a tela
            // esperaria a soma em vez do maior.
            const [detail, targets, days] = await Promise.all([
                detailApi.fetchCollaborator(patientId),
                detailApi.fetchTargets(patientId).catch(() => []),
                detailApi.fetchHistoryDays(patientId)
            ]);
            if (token !== this.pending) {
                return;
            }
            this.targets = targets ?? [];
            // A meta de SONO da pessoa vai para o resumo do grafico de sono (B-41).
            this.analyticsPresenter.setSleepGoal(this.targets.find((target) => target.measurementTypeId === 204)?.value);
            // Mais recente primeiro - o `defaultOrderBy` do `tableHistoryDays` antigo.
            // Comparacao de string ISO, que ja e cronologica, e nao `new Date()`: a
            // data vem sem fuso e o `Date` a jogaria para o dia anterior.
            this.days = [...(days.elements ?? [])].sort((left, right) => right.date.localeCompare(left.date));
            this.showDetail(detail);
        }
        catch (caught) {
            if (token !== this.pending) {
                return;
            }
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar o colaborador.';
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    /** O pouco que a linha da lista ja sabe, na tela antes da resposta. */
    showRow(row) {
        if (!row) {
            return;
        }
        this.scope.name = row.patientName || '-';
        this.scope.cpf = row.cpf || '-';
        this.scope.company = row.companyName ?? undefined;
    }
    showDetail(detail) {
        this.scope.name = detail.socialName || detail.fullName || '-';
        this.scope.email = detail.email || '-';
        this.scope.cpf = detail.cpf || '-';
        this.scope.company = detail.companyName || undefined;
        this.scope.active = detail.active;
        this.scope.photo = fotoUtilizavel(detail.profilePhoto);
        /*
         * Os DEZ campos de "DADOS BÁSICOS", na ordem da referencia.
         *
         * A view corta em duas linhas de cinco - e assim que a tela os dispoe, e as
         * duas grades sao `md:grid-cols-3 lg:grid-cols-5`.
         */
        // A idade decide as zonas de FC do grafico ampliado (B-43).
        this.analyticsPresenter.setBirthDate(detail.birthdayDate ?? undefined);
        this.scope.identification = [
            this.buildField('NOME COMPLETO', detail.fullName || `${detail.firstName} ${detail.lastName}`),
            this.buildField('TELEFONE', detail.telephoneNumber),
            this.buildField('E-MAIL', detail.email),
            this.buildField('DATA NASCIMENTO', detail.birthdayDate ? formatDate(detail.birthdayDate) : null),
            this.buildField('GÊNERO', sexLabel(detail.sex)),
            // O nome social tambem e o nome EXIBIDO acima, mas continua listado aqui
            // como o painel antigo o lista: quem confere um cadastro precisa ver os
            // dois campos, e nao adivinhar qual dos dois o titulo esta mostrando.
            this.buildField('NOME SOCIAL', detail.socialName),
            this.buildField('CONTATO DE EMERGÊNCIA', detail.emergencyContactName),
            this.buildField('TELEFONE DE EMERGÊNCIA', detail.emergencyContactPhone),
            this.buildField('ALTURA', detail.anamnese?.height ? formatHeight(detail.anamnese.height) : null),
            // `weigth` e o typo do backend, nao um erro de digitacao daqui.
            this.buildField('PESO', detail.anamnese?.weigth ? `${detail.anamnese.weigth} Kg` : null)
        ];
        this.showAnamnese(detail);
        /*
         * As metas do colaborador, com a unidade a direita.
         *
         * A referencia converte distancia para KM e sono para horas, e poe a unidade
         * num `span` de ACENTO ao lado do numero - `suffix` no `InfoField`.
         */
        /*
         * Uma meta por tipo, com o rotulo, o valor e o sufixo do `handleTargets` da
         * referencia.
         *
         * O rotulo e o nome que o BACKEND da ao indicador, e nao o da tabela local:
         * la a meta de calorias se chama "Calorias" e a tabela local diz "Gasto
         * Calórico". O valor vai CRU, sem unidade colada - a unidade e o sufixo
         * em ACENTO a direita (era roxo ate a pele do app entrar, em 23/09/2026).
         *
         * Um tipo fora dos quatro nao aparece: la os `if` nao tem `else`.
         */
        this.scope.targets = this.targets.flatMap((target) => {
            const typeId = target.measurementTypeId;
            const name = target.measurementTypeName;
            const acento = '#34E0D2';
            // Passos: o sufixo e o proprio nome do indicador, em maiuscula.
            if (typeId === 201) {
                return [
                    this.buildField(name, String(target.value), { suffix: name.toUpperCase(), suffixColor: acento })
                ];
            }
            if (typeId === 202) {
                return [this.buildField(name, String(target.value), { suffix: 'KCAL', suffixColor: acento })];
            }
            if (typeId === 203) {
                return [this.buildField(name, String(target.value / 1000), { suffix: 'KM', suffixColor: acento })];
            }
            if (typeId === 204) {
                return [this.buildField(name, String(target.value / 60 / 60), { suffix: 'HR', suffixColor: acento })];
            }
            return [];
        });
    }
    /**
     * Os cinco blocos da anamnese, com os titulos e as perguntas da referencia.
     *
     * Um bloco por assunto, e nao uma lista de pares: a pergunta inteira e o rotulo
     * ("VOCÊ PRATICA ALGUMA ATIVIDADE FÍSICA?"), e o agrupamento e o que da sentido
     * a ela. O porte tinha onze pares soltos com rotulos curtos.
     */
    showAnamnese(detail) {
        const anamnese = detail.anamnese;
        if (!anamnese) {
            this.scope.anamneseSections = [];
            return;
        }
        const sections = [
            this.buildAnamneseSection('DADOS DE ATIVIDADE FÍSICA', 'grid grid-cols-1 gap-4 md:grid-cols-2', [
                this.buildField('VOCÊ PRATICA ALGUMA ATIVIDADE FÍSICA?', physicalActivityLabel(anamnese.physicalActivity)),
                // A pergunta dos dias so aparece para quem respondeu que pratica.
                ...(anamnese.physicalActivity === 'YES'
                    ? [
                        this.buildField('Nº DE DIAS QUE VOCÊ PRATICA ESSA ATIVIDADE', daysOfActivityLabel(anamnese.daysOfPhysicalActivity))
                    ]
                    : [])
            ]),
            this.buildAnamneseSection('CONSUMO DE CIGARRO', '', [
                this.buildField('ATUALMENTE VOCÊ FUMA?', smokingLabel(anamnese.smoking))
            ]),
            // `alchoolicDrink` e o typo do backend, idem.
            this.buildAnamneseSection('CONSUMO DE BEBIDAS ALCOÓLICAS', '', [
                this.buildField('FREQUÊNCIA DE CONSUMO', alcoholLabel(anamnese.alchoolicDrink))
            ]),
            this.buildAnamneseSection('ESTADO DE SAÚDE', 'grid grid-cols-1 gap-4 md:grid-cols-3', [
                this.buildField('ESTADO DE SAÚDE ATUAL', healthStateLabel(anamnese.healthState)),
                this.buildField('FREQUÊNCIA QUE REALIZA CONSULTAS / EXAMES', medicalConsultationsLabel(anamnese.medicalConsultationsAndLaboratoryTests)),
                this.buildField('POSSUI ALGUM PLANO DE SAÚDE?', healthPlanLabel(anamnese.healthPlan))
            ]),
            this.buildAnamneseSection('SAÚDE BUCAL', '', [
                this.buildField('FREQUÊNCIA QUE ESCOVA OS DENTES POR DIA', mouthHealthLabel(anamnese.mouthHealth))
            ])
        ];
        this.scope.anamneseSections = sections;
    }
    buildAnamneseSection(title, grid, fields) {
        const scope = new AnamneseSectionScope();
        scope.identity = title;
        scope.title = title;
        scope.grid = grid;
        scope.fields = fields;
        scope.update = this.update;
        return scope;
    }
    buildField(label, value, unit) {
        const scope = new InfoFieldScope();
        scope.identity = label;
        scope.label = label;
        scope.value = value === undefined || value === null || value === '' ? '-' : String(value);
        scope.suffix = unit?.suffix;
        scope.suffixColor = unit?.suffixColor;
        scope.update = this.update;
        return scope;
    }
    /**
     * Abre o detalhamento de uma celula: as leituras cruas que ela resumiu.
     *
     * O dialogo e o do componente, o mesmo do historico compartilhado; daqui sai so a
     * busca, pelo paciente, e o que dizer quando ela falha.
     */
    async openBucket(typeId, hour) {
        const patientId = this.patientId;
        if (!patientId) {
            return;
        }
        try {
            await this.bucketPresenter.open(typeId, hour, () => detailApi.fetchBucketReadings(patientId, typeId, hour));
        }
        catch (caught) {
            LOG.warn('Could not load the bucket readings', caught);
            this.app.alert('error', 'Detalhamento', 'Não foi possível carregar as medições desta hora.');
        }
    }
    /**
     * O relatorio do dia aberto, em PDF: o resumo por hora e TODAS as leituras.
     *
     * O resumo e o que a tela ja tem. As leituras cruas nao: a tabela do dia chega
     * agrupada, e o servidor so as devolve por celula - entao sao buscadas aqui, uma
     * requisicao por celula com dado, `REPORT_CONCURRENCY` por vez. Uma celula que
     * falhe derruba o relatorio inteiro, de proposito: um relatorio de "todos os
     * eventos" com buraco calado diria menos do que diz.
     *
     * Paciente e dia sao lidos NA HORA DO CLIQUE: se o leitor trocar de dia no meio
     * da busca, o PDF que sai e o do dia que ele pediu.
     */
    async onDownloadDay() {
        const patientId = this.patientId;
        const day = this.day;
        if (!patientId || !day || this.scope.downloadingDay) {
            return;
        }
        const entries = this.readings.get(day) ?? [];
        const jobs = dayReportJobs(entries);
        this.scope.downloadingDay = true;
        this.scope.dayReportProgress = `0 de ${jobs.length}`;
        try {
            const fetched = new Map();
            let done = 0;
            let next = 0;
            // Na primeira falha os outros param de pedir: o relatorio ja nao sai, e
            // seguir so escreveria progresso depois de o `finally` o ter limpado.
            let failed = false;
            const worker = async () => {
                while (!failed && next < jobs.length) {
                    const job = jobs[next++];
                    try {
                        const readings = await detailApi.fetchBucketReadings(patientId, job.typeId, job.hour);
                        fetched.set(bucketKey(job.hour, job.typeId), readings);
                    }
                    catch (caught) {
                        failed = true;
                        throw caught;
                    }
                    if (!failed) {
                        this.scope.dayReportProgress = `${++done} de ${jobs.length}`;
                    }
                }
            };
            await Promise.all(Array.from({ length: Math.min(REPORT_CONCURRENCY, jobs.length) }, worker));
            const hours = dayReportHours(entries, fetched);
            // A biblioteca de PDF desce AGORA, como no relatorio de sono.
            const { downloadDayReport } = await import('./day-report.pdf');
            await downloadDayReport({
                patientName: this.scope.name,
                company: this.scope.company,
                dayLabel: formatDate(day),
                day,
                columns: DAY_REPORT_COLUMNS.map((column) => column.label),
                hours
            });
        }
        catch (caught) {
            this.app.unexpected('Gerar o relatório do dia', caught);
        }
        finally {
            this.scope.downloadingDay = false;
            this.scope.dayReportProgress = '';
        }
    }
    /**
     * O historico na tela: as LEITURAS do dia aberto, ou a lista de DIAS.
     *
     * A referencia troca a tela inteira quando um dia e clicado, e nao expande uma
     * linha - por isso uma tabela so serve aos dois estados, mudando as colunas.
     */
    showHistory() {
        const open = this.day;
        // A data da lista de dias ("Última atualização") e a hora das leituras ("Hora
        // Monitoramento") sao o mesmo campo, com textos de tamanhos diferentes: cada uma
        // das duas tabelas tem as suas medidas, tomadas uma vez so.
        this.table.useMeasureSet(open ? 'readings' : 'days');
        // A primeira coluna - a data, ou a hora - nao sai do lugar; as outras, na ordem escolhida.
        const [first, ...movable] = open ? READING_FIELDS : DAY_FIELDS;
        const order = this.table.preferences.arrange(movable.map((column) => column.field));
        const fields = [first, ...order.map((field) => movable.find((column) => column.field === field))];
        this.scope.openDay = open ?? '';
        this.scope.openDayLabel = open ? formatDate(open) : '';
        this.scope.historyColumns = buildHistoryColumns(fields, this.historySort, this.update, (field) => this.onSortHistory(field), (source, target) => this.onMoveHistoryColumn(source, target), this.scope.historyColumns);
        // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
        const rows = open
            ? reuseRows(this.scope.historyRows, this.readings.get(open) ?? [], (entry) => entry.date, (entry, reuse) => buildReadingRow(entry, this.update, (typeId, hour) => this.openBucket(typeId, hour), fields, reuse))
            : reuseRows(this.scope.historyRows, this.days, (entry) => entry.date, (entry, reuse) => buildDayRow(entry, this.update, () => this.onOpenDay(entry.date.slice(0, 10)), fields, reuse));
        this.table.setVisible(fields.map((column) => column.field));
        this.table.setSort(this.historySort ?? DEFAULT_HISTORY_SORT);
        const ordered = this.table.sortRows(rows);
        // Cinquenta por pagina nas duas tabelas, como o `rowsPerPage={50}` de la.
        const pageCount = Math.max(1, Math.ceil(ordered.length / PER_PAGE));
        const page = Math.min(Math.max(1, this.historyPage), pageCount);
        this.historyPage = page;
        this.scope.historyPage = page;
        this.scope.historyPageCount = pageCount;
        this.scope.historyRows = ordered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
    }
    /** Busca as leituras de um dia, se ainda nao estiverem em maos. */
    async loadDay(day) {
        if (!this.patientId || this.readings.has(day)) {
            this.showHistory();
            return;
        }
        this.scope.loadingDay = true;
        this.showHistory();
        try {
            const page = await detailApi.fetchHistoryOfDay(this.patientId, day);
            this.readings.set(day, page.elements ?? []);
        }
        catch (caught) {
            this.app.unexpected('Carregando as medições do dia', caught);
            this.readings.set(day, []);
        }
        finally {
            this.scope.loadingDay = false;
            this.showHistory();
        }
    }
    // ========== ACOES ==========
    /**
     * Abre um dia, e fecha o que estiver aberto.
     *
     * Nao e uma viagem: e o mesmo place com outro parametro, entao move-se o estado
     * e pede-se o endereco de novo. Dar flip para o place onde ja se esta caminharia
     * a arvore inteira da raiz para chegar na mesma conclusao.
     *
     * Trocar de dia zera a pagina e a ordenacao da tabela: as colunas sao outras, e
     * uma ordenacao por "Sono" nao existe dentro do dia.
     */
    async onOpenDay(day) {
        this.day = day;
        this.historySort = undefined;
        this.historyPage = 1;
        await this.loadDay(day);
        this.updateHistory();
    }
    async onCloseDay() {
        this.day = undefined;
        this.historySort = undefined;
        this.historyPage = 1;
        this.showHistory();
        this.updateHistory();
    }
    /** Um clique na coluna ordena por ela; o segundo inverte. */
    async onSortHistory(field) {
        const current = this.historySort ?? DEFAULT_HISTORY_SORT;
        this.historySort = current === field ? `-${field}` : field;
        this.historyPage = 1;
        this.showHistory();
        this.updateHistory();
    }
    /**
     * Uma coluna arrastada e solta sobre outra.
     *
     * Nao e viagem nem vai para o endereco: e preferencia de quem le, e nao estado
     * da tela - um link mandado a um colega abre na ordem DELE.
     */
    async onMoveHistoryColumn(source, target) {
        const movable = DAY_FIELDS.slice(1).map((column) => column.field);
        if (this.table.preferences.move(movable, source, target)) {
            this.showHistory();
        }
    }
    async onMoveHistoryPage(delta) {
        await this.onGoToHistoryPage(this.scope.historyPage + delta);
    }
    async onGoToHistoryPage(page) {
        this.historyPage = page;
        this.showHistory();
        this.updateHistory();
    }
    /**
     * Volta para a lista que hospeda esta montagem, com os filtros que ela tem.
     *
     * Quem sabe qual lista e a montagem - este presenter nao pode saber, ou deixa de
     * ser montavel noutro lugar.
     *
     * O intent tem de ser NOVO, e nao o de chegada reaproveitado. A razao e sutil e
     * custou um teste: uma classe de keys so usa o proprio `place` ao CRIAR um
     * intent - envolver um intent existente nao muda o place dele, e dar `flip` no
     * intent do detalhe vestido das keys da lista voltava para o detalhe, que era
     * exatamente onde se estava.
     *
     * `XxxKeys.of` cria esse intent novo, e quem o preenche e o framework: ele pede a
     * cada presenter vivo o que ele mostra, e o da LISTA esta vivo - o detalhe e um
     * place DENTRO dela nas duas montagens, e e por isso que voltar e instantaneo e
     * a lista reaparece filtrada. Nao ha nada a copiar campo a campo.
     */
    async onBack() {
        await this.mount.back(this.app);
    }
}
/**
 * A foto do colaborador, quando ha uma que o `<img>` consiga carregar.
 *
 * `patient.profile_photo` guarda o que o app manda em `photoBase64`, e a tela usa o valor
 * COMO `src`. Mas a coluna tem lixo antigo: sete pacientes da base de QA carregam textos de
 * cinco caracteres - `66965`, `50726` -, que parecem id de alguma coisa. `<img src="66965">`
 * vira um caminho relativo, o navegador busca, nao acha, e desenha o icone de imagem
 * quebrada bem no lugar onde deveriam estar as iniciais da pessoa.
 *
 * O recuo para as iniciais ja existia na view; ele so nao disparava, porque `"66965"` e um
 * texto verdadeiro. Decidir aqui e o certo: o contrato do scope e "uma foto para mostrar", e
 * um id nao e uma.
 */
function fotoUtilizavel(valor) {
    const foto = valor?.trim();
    if (!foto) {
        return undefined;
    }
    // Os dois formatos que um `src` carrega sem mais nada: a imagem embutida e a remota.
    return foto.startsWith('data:image/') || foto.startsWith('http') ? foto : undefined;
}
//# sourceMappingURL=patient-detail.presenter.js.map